import { MAX_PREVIEW_SIDE } from "components/SmartSurveyor/ReviewPage/FloorPlanPanelPdf/constants";
import {
  NOTE_ATLAS_MAX_WIDTH,
  NOTE_CROP_SIDE,
} from "../appendNotePages/constants";
import { computeCropRegion } from "../computeCropRegion";
import { computeNotePageScale } from "../computeNotePageScale";
import { packCropCells } from "../packCropCells";
import type { CaptureNoteCrops } from "./types";

export const captureNoteCrops: CaptureNoteCrops = ({
  sourceCanvas,
  entries,
  pdfPageWidth,
  pdfPageHeight,
}) => {
  const pageScale = computeNotePageScale({ pdfPageWidth, pdfPageHeight });
  const renderScale = Math.min(
    MAX_PREVIEW_SIDE / pdfPageWidth,
    MAX_PREVIEW_SIDE / pdfPageHeight,
  );
  const cropDisplayPx = NOTE_CROP_SIDE * pageScale * renderScale;
  const sourceScaleX = sourceCanvas.width / pdfPageWidth;
  const sourceScaleY = sourceCanvas.height / pdfPageHeight;
  const sourcePxPerPt = Math.min(sourceScaleX, sourceScaleY);

  const regions = entries.map((entry) =>
    computeCropRegion({
      bbox: entry.bbox,
      pdfPageWidth,
      pdfPageHeight,
      cropDisplayPx,
      sourcePxPerPt,
    }),
  );
  // A cell never holds more pixels than the page will show, so the atlas stays small.
  const sides = regions.map((region) =>
    Math.max(
      1,
      Math.round(Math.min(region.side * sourcePxPerPt, cropDisplayPx)),
    ),
  );
  const { cells, width, height } = packCropCells({
    sides,
    maxWidth: NOTE_ATLAS_MAX_WIDTH,
  });

  const atlas = document.createElement("canvas");
  atlas.width = width;
  atlas.height = height;
  const context = atlas.getContext("2d");
  if (!context) {
    throw new Error("Failed to get 2D context for note crops canvas");
  }
  context.imageSmoothingQuality = "high";
  regions.forEach((region, index) => {
    const cell = cells[index];
    context.drawImage(
      sourceCanvas,
      region.x * sourceScaleX,
      region.y * sourceScaleY,
      region.side * sourceScaleX,
      region.side * sourceScaleY,
      cell.x,
      cell.y,
      cell.side,
      cell.side,
    );
  });

  return { atlas, regions, cells };
};
